<template>
  <!-- eslint-disable vue/no-use-v-if-with-v-for,vue/no-confusing-v-for-v-if -->
  <v-sheet height="100%" color="bg1" width="100%" id="admin-projects-container">
    <v-sheet height="100%" class="rounded-tl-lg pa-4">
      <div
        class="pa-2 d-flex align-center"
        style="gap: 8px"
        :class="{
          'flex-wrap': $vuetify.breakpoint.xs,
        }">
        <h3>Projects</h3>
      </div>
      <div class="pa-4">
        <div class="d-flex justify-space-between algin-center" style="gap: 0.75rem">
          <v-sheet :width="$vuetify.breakpoint.xs ? '100%' : ''" style="flex-grow: 1;">
            <v-text-field
              outlined
              dense
              hide-details
              clearable
              v-virtual-keyboard
              append-icon="mdi-magnify"
              label="Search"
              placeholder="Search..."
              v-model="searchtext">
            </v-text-field>
          </v-sheet>
          <div class="d-flex align-center" style="gap:8px">
            <v-btn v-has="permissions.Admin360Images.Projects" color="primary" @click="addProject()">
              <v-icon class="me-2">mdi-plus</v-icon> Add Project
            </v-btn>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="projectsLoading" class="d-flex align-center justify-center py-16">
          <AvaLoader type="pulse" size="large" text="Loading projects..." />
        </div>

        <!-- Grid View - Projects Table -->
        <div v-else-if="viewMode === 'grid'" class="py-4">
          <v-data-table
            :headers="projectHeaders"
            :items="shownProjects"
            :search="searchtext"
            item-key="id"
            show-expand
            :expanded.sync="expandedProjects"
            class="admin-projects-table"
            dense
            hide-default-footer
            disable-pagination>
            <!-- Project Name Column -->
            <template #[`item.name`]="{ item }">
              <strong
                class="clickable-project-name"
                @click="toggleProjectExpansion(item)">
                {{ item.name }}
              </strong>
            </template>

            <!-- Image Count Column -->
            <template #[`item.imageCount`]="{ item }">
              <v-chip small color="primary">
                {{ item.images ? item.images.length : 0 }} images
              </v-chip>
            </template>

            <!-- Status Column -->
            <template #[`item.isArchived`]="{ item }">
              <v-chip v-if="item.isArchived" x-small color="error" dark>
                <v-icon x-small class="mr-1">mdi-archive</v-icon>
                Archived
              </v-chip>
              <span v-else class="grey--text">-</span>
            </template>

            <!-- Actions Column -->
            <template #[`item.actions`]="{ item }">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on">
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <!-- Upload Images 360 -->
                  <v-list-item
                    v-has="permissions.Admin360Images.Upload"
                    @click="showUploadImagesUI(item)">
                    <v-list-item-icon>
                      <v-icon small>mdi-upload</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Upload Images 360</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider></v-divider>

                  <!-- Run AI -->
                  <v-list-item
                    v-if="canRunAiAnalysis"
                    @click="processImages360(item)"
                    :disabled="processingAnalysisJob">
                    <v-list-item-icon>
                      <v-icon small color="success">mdi-play</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Run AI</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <!-- Post Process -->
                  <v-list-item
                    v-has="permissions.AI.Access"
                    @click="postProcessImages360(item)"
                    :disabled="!postProcessAvailable(item) || postProcessLoadingIds.includes(item.id)">
                    <v-list-item-icon>
                      <v-icon small color="success">mdi-wrench-cog</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Post Process</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider></v-divider>

                  <!-- Edit Project -->
                  <v-list-item
                    @click="editProject(item)"
                    :disabled="!$has(permissions.Admin360Images.Projects)">
                    <v-list-item-icon>
                      <v-icon small>mdi-pencil</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Edit Project</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <!-- Delete Project -->
                  <v-list-item
                    @click="deleteProject(item)"
                    :disabled="!$has(permissions.Admin360Images.Projects)">
                    <v-list-item-icon>
                      <v-icon small color="error">mdi-delete</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="error--text">Delete Project</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <!-- Upload Progress / Expand Button -->
            <template #[`item.data-table-expand`]="{ item, isExpanded, expand }">
              <div class="d-flex align-center">
                <v-progress-circular
                  v-if="processingAnalysisJob || postProcessLoadingIds.includes(item.id)"
                  indeterminate
                  size="20"
                  width="2"
                  color="primary"
                  class="mr-2"></v-progress-circular>
                <v-btn
                  icon
                  small
                  @click="expand(!isExpanded)">
                  <v-icon small>
                    {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                </v-btn>
              </div>
            </template>

            <!-- Expanded Row - Images Table -->
            <template #expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-0">
                <div v-if="item.images && item.images.length > 0" class="images-table-container">
                  <!-- Selection Banner for Images -->
                  <v-slide-y-transition>
                    <div
                      v-if="selectedImages[item.id] && selectedImages[item.id].length > 0"
                      class="images-selection-banner d-flex align-center justify-space-between mb-2">
                      <span class="text-caption selection-count">
                        <strong>{{ selectedImages[item.id].length }}</strong> selected
                      </span>
                      <div class="d-flex align-center" style="gap: 8px;">
                        <v-btn
                          x-small
                          text
                          @click="clearProjectSelection(item.id)">
                          <v-icon x-small class="mr-1">mdi-close</v-icon>
                          Discard
                        </v-btn>
                        <v-btn
                          v-has="permissions.Admin360Images.Delete"
                          x-small
                          text
                          @click="deleteSelectedProjectImages(item)">
                          <v-icon x-small class="mr-1">mdi-delete-outline</v-icon>
                          Delete
                        </v-btn>
                      </div>
                    </div>
                  </v-slide-y-transition>
                  <v-data-table
                    :headers="imageHeaders"
                    :items="item.images"
                    item-key="id"
                    class="nested-images-table"
                    dense
                    hide-default-footer
                    disable-pagination
                    show-select
                    v-model="selectedImages[item.id]"
                    @item-selected="updateSelectedItems"
                    @toggle-select-all="updateSelectedItems">
                    <!-- Thumbnail Column -->
                    <template #[`item.thumbnailURL`]="{ item: image }">
                      <div class="image-thumbnail-wrapper">
                        <v-img
                          :src="image.thumbnailURL"
                          :alt="image.name"
                          width="60"
                          height="60"
                          class="image-thumbnail">
                          <template v-slot:placeholder>
                            <v-row class="fill-height ma-0" align="center" justify="center">
                              <v-icon>mdi-image</v-icon>
                            </v-row>
                          </template>
                        </v-img>
                        <v-icon
                          v-if="image.latLong"
                          small
                          class="gps-badge"
                          color="success">
                          mdi-crosshairs-gps
                        </v-icon>
                      </div>
                    </template>

                    <!-- Name Column -->
                    <template #[`item.name`]="{ item: image }">
                      {{ image.name }}
                    </template>

                    <!-- Date Column -->
                    <template #[`item.createDate`]="{ item: image }">
                      {{ $formatDate(image.createDate) }}
                    </template>

                    <!-- Insights Column -->
                    <template #[`item.insights`]="{ item: image }">
                      <v-chip
                        v-if="image.anomalyInsights && image.anomalyInsights.length > 0"
                        x-small
                        color="warning">
                        {{ image.anomalyInsights.length }} Inspects
                      </v-chip>
                    </template>

                    <!-- Elevation Column -->
                    <template #[`item.elevation`]="{ item: image }">
                      <v-btn
                        v-if="image.latLong && image.forgeCameraPosition"
                        x-small
                        text
                        @click="clickOnImage(item, image)">
                        {{ image.forgeCameraPosition.z || 0 }}
                      </v-btn>
                      <span v-else>-</span>
                    </template>

                    <!-- Actions Column -->
                    <template #[`item.actions`]="{ item: image }">
                      <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            x-small
                            v-bind="attrs"
                            v-on="on">
                            <v-icon small>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item
                            v-has="permissions.Admin360Images.Delete"
                            @click="deleteImage(item, image)">
                            <v-list-item-icon>
                              <v-icon small color="error">mdi-delete</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="error--text">Delete</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </template>
                  </v-data-table>
                </div>
                <div v-else class="pa-4 text-center grey--text">
                  No images available for this project
                </div>
              </td>
            </template>
          </v-data-table>
        </div>

        <!-- Tree View -->
        <ProjectsTreeView
          v-else-if="viewMode === 'tree'"
          :items="treeViewItems"
          :search="searchtext"
          :loading="treeLoading"
          :processing-analysis-job="processingAnalysisJob"
          :post-process-loading-ids="postProcessLoadingIds"
          :permissions="permissions"
          :point-clouds="pointClouds"
          :selected-images="selectedImages"
          @upload="showUploadImagesUI"
          @run-ai="processImages360"
          @post-process="postProcessImages360"
          @move="openMoveDialog"
          @edit="editProject"
          @delete="deleteProject"
          @toggle-archive="toggleArchive"
          @add-folder="addFolder"
          @update:selected-images="handleTreeSelectionUpdate"
          @clear-selection="onClearSelection"
          @delete-selected="deleteAllSelectedImages"
          @delete-image="deleteImage"
        />
      </div>
    </v-sheet>
    <UploadImages ref="UploadImages"></UploadImages>
    <PostProcessDialog ref="postProcessDialog"></PostProcessDialog>
    <ProjectDialog ref="projectDialog" @save="handleProjectSave"></ProjectDialog>
    <MoveProjectDialog ref="moveProjectDialog" @confirm="handleMoveProject"></MoveProjectDialog>
  </v-sheet>
</template>

<script>
import aiAnalysisPermissionMixin from '@/features/Dashboard/utils/aiAnalysisPermissionMixin';
  import { toggleExpandComponent } from '@/scripts/js/forgeviewer';
  import { ForgeProject } from '@/scripts/js/forgeModels';
  import { saveProjects, editType } from '@Utils/mongoUtils';
  import { $has, $formatDate } from '@/utils';
  import UploadImages from '@/components/admin/popups/uploadImages.vue';
  import permissions from '@Share/config/permissions';
  import projectsService from '../services/projects.service';
  import PostProcessDialog from '@/features/Admin/components/PostProcessDialog.vue';
  import ProjectDialog from '@/features/Admin/components/ProjectDialog.vue';
  import MoveProjectDialog from '@/features/Admin/components/MoveProjectDialog.vue';
  import ProjectsTreeView from '@/features/Admin/components/ProjectsTreeView.vue';
  import { AvaLoader } from '@/components/loaders';

  export default {
    mixins: [aiAnalysisPermissionMixin],
    name: 'AdminProjects',
    components: {
      UploadImages,
      PostProcessDialog,
      ProjectDialog,
      MoveProjectDialog,
      ProjectsTreeView,
      AvaLoader,
    },
    data() {
      return {
        processingAnalysisJob: false,
        permissions,
        isExpanded: false,
        searchtext: '',
        isTranslating: false,
        postProcessLoadingIds: [],
        expandedProjects: [],
        selectedImages: {},
        selected_items: [],
        viewMode: 'tree',
        treeData: [],
        treeLoading: false,
        projectsLoading: true,
        projectHeaders: [
          { text: 'Project Name', value: 'name', sortable: true },
          { text: 'Images', value: 'imageCount', sortable: true },
          { text: 'Status', value: 'isArchived', sortable: true, width: '100px' },
          { text: 'Actions', value: 'actions', sortable: false , width: '80px' },
          { text: '', value: 'data-table-expand', sortable: false, width: '50px' },
        ],
        imageHeaders: [
          { text: 'Thumbnail', value: 'thumbnailURL', sortable: false, width: '80px' },
          { text: 'Name', value: 'name', sortable: true },
          { text: 'Date', value: 'createDate', sortable: true },
          { text: 'Insights', value: 'insights', sortable: false  },
          { text: 'Elevation', value: 'elevation', sortable: false  },
          { text: '', value: 'actions', sortable: false, width: '50px' },
        ],
      };
    },
    props: ['projects'],
    methods: {
      $has,
      $formatDate,
      initializeSelectedItems() {
        this.projects.forEach(project => {
          if (project.images) {
            const selected = project.images.filter(img => img.isSelected);
            if (selected.length > 0) {
              this.$set(this.selectedImages, project.id, selected);
            }
          }
        });
        this.updateSelectedItems();
      },

      /**
       * Fetch tree structure from API. The server returns nested
       * folder/project nodes with parentId/hasImages/children.
       */
      async fetchTreeData() {
        const assetId = this.$store.state.assetId;
        if (!assetId) return;

        this.treeLoading = true;
        try {
          const response = await projectsService.getTree(assetId);
          this.treeData = response.data || [];
        } catch (err) {
          console.error('Failed to fetch tree data:', err);
          this.treeData = [];
        } finally {
          this.treeLoading = false;
        }
      },

      /**
       * Normalize API tree nodes into the shape v-treeview expects.
       * Recursively maps server nodes to { id, name, type, children, ... }.
       */
      normalizeTreeNodes(nodes) {
        if (!Array.isArray(nodes)) return [];

        return nodes.map(node => {
          // Detect leaf: explicit hasImages flag, or infer from having images
          const hasImages = node.hasImages !== undefined
            ? !!node.hasImages
            : !!(node.images && node.images.length > 0);
          const isLeaf = hasImages;
          const normalized = {
            id: node.id,
            name: node.name || node.projectName || '',
            type: 'project',
            hasImages,
            parentId: node.parentId || null,
            isArchived: node.isArchived || false,
            // Carry the full node as project reference for actions
            project: node,
          };

          if (isLeaf) {
            // Leaf project — images are children in the tree
            const images = node.images || [];
            normalized.imageCount = images.length;
            normalized.images = images;

            if (images.length > 0) {
              normalized.children = images.map(img => ({
                id: img.imageId || img.id,
                name: img.name,
                type: 'image',
                hasInsights: img.anomalyInsights && img.anomalyInsights.length > 0,
                insightsCount: img.anomalyInsights ? img.anomalyInsights.length : 0,
                hasGPS: !!img.latLong,
                image: img,
                project: node,
              }));
            }
            // No children when empty — node renders without expand arrow
          } else {
            // Folder — recurse into children
            const children = node.children || [];
            const normalizedChildren = this.normalizeTreeNodes(children);
            normalized.imageCount = this.countImagesRecursive(normalizedChildren);

            if (normalizedChildren.length > 0) {
              normalized.children = normalizedChildren;
            }
            // No children when empty — node renders without expand arrow
          }

          return normalized;
        });
      },

      /** Count total images across all leaf descendants. */
      countImagesRecursive(nodes) {
        let count = 0;
        for (const node of nodes) {
          if (node.type === 'image') {
            count++;
          } else if (node.children) {
            count += this.countImagesRecursive(node.children);
          }
        }
        return count;
      },

      postProcessAvailable(project) {
        if (!project || !project.name) return false;
        let matchedPc = this.pointClouds.find((o) => o.name == project.name);
        return matchedPc && project.images && project.images.some((o) => o.anomalyInsights?.length > 0);
      },

      onClearSelection() {
        Object.keys(this.selectedImages).forEach(projectId => {
          if (this.selectedImages[projectId]) {
            this.selectedImages[projectId].forEach(img => {
              img.isSelected = false;
            });
          }
        });
        this.selectedImages = {};
        this.selected_items = [];
      },

      toggleProjectExpansion(project) {
        const index = this.expandedProjects.findIndex(p => p.id === project.id);
        if (index > -1) {
          this.expandedProjects.splice(index, 1);
        } else {
          this.expandedProjects.push(project);
        }
      },

      /**
       * Add a root-level project (no parent).
       */
      addProject() {
        this.$refs.projectDialog.open(this.projects, null, {
          createType: 'project',
        });
      },

      /**
       * Add a sub-folder inside an existing folder.
       * Only available on folder nodes (hasImages === false).
       */
      addFolder(parentItem) {
        const siblings = parentItem.children
          ? parentItem.children.filter(c => c.type === 'project')
          : [];
        this.$refs.projectDialog.open(siblings, null, {
          parentProject: parentItem,
          createType: 'folder',
        });
      },

      editProject(item) {
        // item can be either a project from grid view or a tree node
        const project = item.project || item;
        this.$refs.projectDialog.open(this.projects, project);
      },

      openMoveDialog(item) {
        this.$refs.moveProjectDialog.open(item, this.treeViewItems);
      },

      async handleMoveProject({ item, newParentId }) {
        try {
          const project = item.project || item;
          await projectsService.update(project.id, {
            name: project.name,
            parentId: newParentId,
            hasImages: project.hasImages,
            isArchived: project.isArchived,
          });
          const localProject = this.projects.find(p => p.id === project.id);
          if (localProject) {
            localProject.parentId = newParentId;
          }
          if (this.viewMode === 'tree') {
            this.fetchTreeData();
          }
          this.$dialog.notify.success('Project moved successfully.', {
            position: 'top-right',
            timeout: 3000,
          });
        } catch (err) {
          this.$dialog.notify.error('Failed to move project', {
            position: 'top-right',
            timeout: 3000,
          });
          console.error('Move project error:', err);
        }
      },

      handleProjectSave(payload) {
        const { id, name, parentId, hasImages } = payload;
        const facilityAssetId = this.$store.state.assetId;

        if (id) {
          // Edit mode
          projectsService.update(id, { name, parentId: parentId || null })
            .then(() => {
              const project = this.projects.find(p => p.id === id);
              if (project) {
                project.name = name;
                project.parentId = parentId || null;
              }
              if (this.viewMode === 'tree') {
                this.fetchTreeData();
              }
            })
            .catch((err) => {
              this.$dialog.notify.error('Failed to update project', {
                position: 'top-right',
                timeout: 3000,
              });
              console.error('Project update error:', err);
            });
        } else {
          // Create mode
          projectsService.create({
              assetId: facilityAssetId,
              name,
              parentId: parentId || null,
            })
            .then((response) => {
              const newProject = new ForgeProject();
              newProject.id = response.data?.id || response.data?.assetId;
              newProject.name = name;
              newProject.parentId = parentId || null;
              newProject.hasImages = hasImages !== undefined ? hasImages : true;
              this.$emit('update_projects', [...this.projects, newProject]);

              if (this.viewMode === 'tree') {
                this.fetchTreeData();
              }
            })
            .catch((err) => {
              this.$dialog.notify.error('Failed to create project', {
                position: 'top-right',
                timeout: 3000,
              });
              console.error('Project creation error:', err);
            });
        }
      },

      toggleArchive(item) {
        const project = item.project || item;
        const newArchived = !project.isArchived;

        projectsService.update(project.id, {
          name: project.name,
          parentId: project.parentId || null,
          hasImages: project.hasImages,
          isArchived: newArchived,
        })
          .then(() => {
            const local = this.projects.find(p => p.id === project.id);
            if (local) local.isArchived = newArchived;
            if (this.viewMode === 'tree') {
              this.fetchTreeData();
            }
          })
          .catch((err) => {
            this.$dialog.notify.error('Failed to update archive status', {
              position: 'top-right',
              timeout: 3000,
            });
            console.error('Archive toggle error:', err);
          });
      },

      showUploadImagesUI(project) {
        // project can be a tree node or a flat project
        const proj = project?.project || project;
        if (!proj) {
          if (this.$store.state.projects.length == 0) {
            this.$dialog.notify.warning('Please add project first.', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }
          this.$refs.UploadImages.open(this.projects);
        } else {
          this.$refs.UploadImages.open(this.projects, proj);
        }
      },

      setViewMode(mode) {
        this.viewMode = mode;
        localStorage.setItem('adminProjectsViewMode', mode);
        this.projectsLoading = true;
        this.$store.dispatch('GET_PROJECTS').finally(() => {
          this.projectsLoading = false;
        });
      },

      toggleExpanded() {
        toggleExpandComponent(this);
      },

      deleteProject(item) {
        const project = item.project || item;

        this.$dialog.error({
          text: 'Are you sure you want to delete this project and all its contents?',
          title: 'Delete Project',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                projectsService.del(project.id).then(() => {
                  this.$dialog.notify.success('Project deleted successfully.', {
                    position: 'top-right',
                    timeout: 3000,
                  });
                  this.$emit('update_projects', this.projects.filter((o) => o.id !== project.id));
                  // Refresh tree
                  if (this.viewMode === 'tree') {
                    this.fetchTreeData();
                  }
                }).catch(({ response }) => {
                  this.$dialog.notify.error(response?.data ?? 'Error while deleting', {
                    position: 'top-right',
                    timeout: 3000,
                  });
                });
              },
            },
          },
        });
      },

      clickOnImage(project, image) {
        if (!image.latLong || !image.forgeCameraPosition) return;
        this.$store.dispatch({
          type: 'prompt_Forge',
          title: 'Add Image Elevation',
          callback: (elevation) => {
            elevation = parseFloat(elevation);
            if (!elevation && elevation !== 0) {
              this.$dialog.notify.warning('Invalid number!', {
                position: 'top-right',
                timeout: 3000,
              });
              return;
            }
            this.changeImageElevation(project, image, elevation);
          },
          input: {
            inputName: 'Elevation',
            inputValue: image.forgeCameraPosition.z,
          },
        });
      },

      updateSelectedItems() {
        const allSelected = [];
        Object.keys(this.selectedImages).forEach(projectId => {
          if (this.selectedImages[projectId] && Array.isArray(this.selectedImages[projectId])) {
            this.selectedImages[projectId].forEach(img => {
              img.isSelected = true;
              allSelected.push(img);
            });
          }
        });

        this.projects.forEach(project => {
          if (project.images) {
            project.images.forEach(img => {
              const isInSelection = allSelected.some(selected => selected.id === img.id);
              img.isSelected = isInSelection;
            });
          }
        });

        this.selected_items = allSelected;
      },

      clearProjectSelection(projectId) {
        if (this.selectedImages[projectId]) {
          this.selectedImages[projectId].forEach(img => {
            img.isSelected = false;
          });
          this.$set(this.selectedImages, projectId, []);
        }
        this.updateSelectedItems();
      },

      handleTreeSelectionUpdate(newSelection) {
        Object.keys(newSelection).forEach(projectId => {
          this.$set(this.selectedImages, projectId, newSelection[projectId]);
        });
        this.updateSelectedItems();
      },

      deleteAllSelectedImages() {
        const allSelected = [];

        Object.keys(this.selectedImages).forEach(projectId => {
          if (this.selectedImages[projectId] && this.selectedImages[projectId].length > 0) {
            allSelected.push(...this.selectedImages[projectId]);
          }
        });

        if (allSelected.length === 0) return;

        this.$dialog.error({
          text: `Are you sure you want to delete ${allSelected.length} selected image${allSelected.length > 1 ? 's' : ''}?`,
          title: 'Delete Images',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Delete',
              color: 'error',
              handle: () => {
                saveProjects(allSelected, editType.DELETE_IMAGES360);
                allSelected.forEach((img) => {
                  const targetProject = this.projects.find((p) =>
                    p.images && p.images.some(pImg => pImg.id === img.id)
                  );
                  if (targetProject) {
                    const index = targetProject.images.findIndex((o) => o.id === img.id);
                    if (index > -1) {
                      targetProject.images.splice(index, 1);
                    }
                  }
                });
                this.onClearSelection();
              },
            },
          },
        });
      },

      deleteSelectedProjectImages(project) {
        const selectedForProject = this.selectedImages[project.id] || [];
        if (selectedForProject.length === 0) return;

        this.$dialog.error({
          text: `Are you sure you want to delete ${selectedForProject.length} selected image${selectedForProject.length > 1 ? 's' : ''}?`,
          title: 'Delete Images',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Delete',
              color: 'error',
              handle: () => {
                saveProjects(selectedForProject, editType.DELETE_IMAGES360);
                selectedForProject.forEach((img) => {
                  const index = project.images.findIndex((o) => o.id === img.id);
                  if (index > -1) {
                    project.images.splice(index, 1);
                  }
                });
                this.clearProjectSelection(project.id);
              },
            },
          },
        });
      },

      deleteImage(project, image) {
        this.$dialog.error({
          text: `Are you sure you want to delete "${image.name}"?`,
          title: 'Delete Image',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Delete',
              color: 'error',
              handle: () => {
                saveProjects([image], editType.DELETE_IMAGES360);
                const proj = project?.id ? project : this.projects.find(p =>
                  p.images && p.images.some(img => img.id === image.id)
                );
                if (proj && proj.images) {
                  const index = proj.images.findIndex((o) => o.id === image.id);
                  if (index > -1) {
                    proj.images.splice(index, 1);
                  }
                }
                if (this.selectedImages[proj?.id]) {
                  const selIndex = this.selectedImages[proj.id].findIndex(img => img.id === image.id);
                  if (selIndex > -1) {
                    this.selectedImages[proj.id].splice(selIndex, 1);
                    this.updateSelectedItems();
                  }
                }
              },
            },
          },
        });
      },

      changeImageElevation(project, image, elevation) {
        image.forgeCameraPosition.z = elevation;
        saveProjects(image, editType.EDIT_IMAGE360, project.name);
      },

      processImages360(item) {
        if (!this.canRunAiAnalysis) return;
        const project = item.project || item;
        let selectedImages = this.selected_items;
        if (selectedImages.length == 0) {
          selectedImages = project.images || [];
        }

        this.processingAnalysisJob = true;
        this.$store.dispatch('AI_INSPECT/START_ANALYSIS_JOB', {
          imagesIds: selectedImages.map((o) => o.id),
          facilityId: this.$route.params.id
        }).finally(() => {
          this.processingAnalysisJob = false;
        });
      },

      async postProcessImages360(item) {
        const project = item.project || item;
        let selectedImages = this.selected_items;
        if (selectedImages.length == 0) {
          selectedImages = project.images || [];
        }

        if (this.postProcessLoadingIds.length) return;

        this.postProcessLoadingIds.push(project.id);

        let viewer = await this.$store.dispatch('OPEN_ADMIN_3D_VIEWER');
        viewer.hideAll();

        let pc = this.pointClouds.find((o) => o.name == project.name);

        if (!pc) {
          this.$dialog.notify.error('No point cloud found for this project', {
            position: 'top-right',
            timeout: 3000,
          });
          this.postProcessLoadingIds = this.postProcessLoadingIds.filter((o) => o != project.id);
          return;
        }

        this.postProcessLoadingIds = this.postProcessLoadingIds.filter((o) => o != project.id);

        let imagesToProcess = selectedImages.filter((o) => o.anomalyInsights?.length > 0);

        if (imagesToProcess.length == 0) {
          this.$dialog.notify.error('No images with insights found', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        this.$refs.postProcessDialog.openInsightsModal(imagesToProcess, pc);
      },
    },
    computed: {
      shownProjects: function () {
        // Grid view: show only leaf projects (those with images)
        const leafProjects = this.projects.filter(p => p.hasImages !== false);
        return this.$store.state.forgeService.filterLevelsBySearch(leafProjects, this.searchtext);
      },

      /**
       * Tree view items: normalized from server tree data.
       * The server provides the full nested folder structure.
       */
      treeViewItems() {
        return this.normalizeTreeNodes(this.treeData);
      },

      facilityId() {
        return this.$route.params.id;
      },
      pointClouds() {
        return this.$store.state.pointClouds;
      },
      viewer() {
        return this.$store.state.viewer;
      },
      forgeService() {
        return this.$store.state.forgeService;
      },
    },
    watch: {
      viewMode(newMode) {
        if (newMode === 'tree') {
          this.fetchTreeData();
        }
      },
    },
    created() {
      this.onClearSelection();
      this.projectsLoading = true;
      this.$store.dispatch('GET_PROJECTS').finally(() => {
        this.projectsLoading = false;
      });
    },
    async mounted() {
      this.initializeSelectedItems();

      // Fetch tree data if starting in tree mode
      if (this.viewMode === 'tree') {
        this.fetchTreeData();
      }
    },
  };
</script>

<style scoped lang="scss">
  .model-3d {
    background-size: cover;
    background-image: url(../../../images/3d-black.png);
    width: 23px;
    height: 23px;
    margin-right: 10px;
  }

  .admin-projects-table {
    background: transparent;

    ::v-deep .v-data-table__wrapper {
      background: rgba(30, 35, 40, 0.5);
      border-radius: 4px;
    }

    ::v-deep thead {
      background: rgba(20, 25, 30, 0.8);
    }

    ::v-deep tbody tr:hover {
      background: rgba(255, 255, 255, 0.05) !important;
    }

    // Light theme overrides
    .theme--light & {
      ::v-deep .v-data-table__wrapper {
        background: rgba(255, 255, 255, 0.8);
      }

      ::v-deep thead {
        background: rgba(240, 240, 240, 0.9);
      }

      ::v-deep tbody tr:hover {
        background: rgba(0, 0, 0, 0.03) !important;
      }
    }
  }

  .images-table-container {
    padding: 16px;
    background: rgba(20, 25, 30, 0.3);

    .theme--light & {
      background: rgba(245, 245, 245, 0.5);
    }
  }

  .images-selection-banner {
    padding: 6px 12px;
    border-left: 3px solid rgba(255, 255, 255, 0.4);
    background: transparent;

    .selection-count {
      opacity: 0.7;

      strong {
        opacity: 1;
        font-weight: 600;
      }
    }

    .theme--light & {
      border-left-color: rgba(0, 0, 0, 0.3);
    }
  }

  .nested-images-table {
    background: transparent;

    ::v-deep .v-data-table__wrapper {
      background: rgba(40, 45, 50, 0.4);
    }

    ::v-deep thead {
      background: rgba(30, 35, 40, 0.6);
    }

    .theme--light & {
      ::v-deep .v-data-table__wrapper {
        background: rgba(255, 255, 255, 0.6);
      }

      ::v-deep thead {
        background: rgba(230, 230, 230, 0.8);
      }
    }
  }

  .image-thumbnail-wrapper {
    position: relative;
    display: inline-block;
    padding: 4px 0;

    .image-thumbnail {
      border-radius: 4px;
      border: 1px solid rgba(255, 255, 255, 0.1);

      .theme--light & {
        border-color: rgba(0, 0, 0, 0.1);
      }
    }

    .gps-badge {
      position: absolute;
      bottom: 8px;
      right: 4px;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 50%;
      padding: 2px;
    }
  }

  .clickable-project-name {
    cursor: pointer;
    user-select: none;
    transition: color 0.2s ease;

    &:hover {
      color: var(--v-primary-base);
    }
  }
</style>
